/**
 * Choose Your Region Modal component.
 *
 * @author Vladimir Shishlyannikov
 * @created 2024/09/12
 * @copyright 2024-present iWoo Ltd. All rights reserved.
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
// import Cookies from "js-cookie";
import { useAppState } from "../../state";
import { useRegion, getRegion } from "../../context/RegionProvider";

// Components
import Button from "../Button/Button";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Utils
import { clientWidth } from "../../utils";
import { CITY_MAPPING, modalStyles } from "../../constants";

// Icons
import ArrowRightIcon from "../../icons/ArrowRightIcon";

// Images
import Image from "./ChooseYourRegionImage";
import usaFlag from "./usa-flag.png";
import usaFlag2x from "./usa-flag@2x.png";
import usaFlag3x from "./usa-flag@3x.png";
import ukFlag from "./uk-flag.png";
import ukFlag2x from "./uk-flag@2x.png";
import ukFlag3x from "./uk-flag@3x.png";
import GlobeIcon from "./GlobeIcon";
import CloseIcon from "../../icons/CloseIcon";
import ArrowLeftIcon from "../../icons/ArrowLeftIcon";

const RegionItem = ({
  id,
  className,
  active,
  region,
  title,
  flagSrc,
  flagSrcSet,
  onChange,
}) => {
  const handleChange = useCallback(() => {
    onChange && onChange(region);
  }, [onChange, region]);

  return (
    <div
      className={`${className} tbk-cursor-pointer tbk-p-1 ${
        active ? "tbk-bg-basic-white" : ""
      }`}
      onClick={handleChange}
    >
      <input
        type="radio"
        id={id}
        checked={active}
        onChange={handleChange}
        className="tbk-hidden"
      />
      <label
        className="tbk-mb-0 tbk-flex tbk-items-center tbk-pl-4"
        htmlFor={id}
      >
        <img
          src={flagSrc}
          srcSet={flagSrcSet}
          alt={region}
          style={{ width: "24px", height: "16px" }}
        />
        <div className="tbk-text-main tbk-ml-2 tbk-text-primary">{title}</div>
      </label>
    </div>
  );
};

const CityItem = ({ id, className, active, city, onChange }) => {
  const handleChange = useCallback(() => {
    onChange && onChange(CITY_MAPPING[city]);
  }, [onChange, city]);

  return (
    <div
      className={`${className} tbk-cursor-pointer tbk-p-1 ${
        active ? "tbk-bg-basic-white" : ""
      }`}
      onClick={handleChange}
    >
      <input
        type="radio"
        id={id}
        checked={active}
        onChange={handleChange}
        className="tbk-hidden"
      />
      <label
        className="tbk-mb-0 tbk-flex tbk-items-center tbk-pl-4"
        htmlFor={id}
      >
        <div className="tbk-text-main tbk-text-primary">{city}</div>
      </label>
    </div>
  );
};

export const RegionSelectModal = ({
  isOpen,
  region,
  city,
  openModal: openModalGlobal,
  locations,
  locationsLoading,
  onSelectRegion,
  onClose,
}) => {
  const { theme } = useTheme();
  const { user, updateCustomerInfo, clearUpcomingEventsLoaded } = useAppState();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState("region");
  const [selectedRegion, setSelectedRegion] = useState(region || null);
  const [selectedCity, setSelectedCity] = useState(city || null);

  const modalResolveRef = useRef();

  const openModal = () => {
    return new Promise((resolve) => {
      modalResolveRef.current = resolve;
      setIsModalOpen(true);
    });
  };

  const closeModal = () => {
    if (modalResolveRef.current) {
      modalResolveRef.current();
      modalResolveRef.current = null;
      onClose && onClose();
    }
    setIsModalOpen(false);
  };

  // Проверяем нужно ли автоматически открыть модалку
  useEffect(() => {
    if (!isOpen && user?.region === "US" && !user?.city) {
      const timer = setTimeout(() => {
        openModalGlobal();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [/*isOpen, */ user]);

  useEffect(() => {
    if (isOpen) {
      if (region === "US") {
        // setCurrentStep("city");
      }

      setSelectedRegion(region || null);
      setSelectedCity(city || null);
      openModal();
    } else {
      closeModal();
    }
  }, [isOpen]);

  const selectedRegionData = locations.find((c) => c.code === selectedRegion);
  const hasCities = false; //selectedRegionData?.cities?.length > 2;

  const handleRegionSelect = (regionCode) => {
    setSelectedRegion(regionCode);
  };
  useEffect(() => {
    // Здесь нужна своя проверка на города?
    const _hasCities = false; //selectedRegionData?.cities?.length > 2;

    if (!_hasCities && selectedRegionData) {
      // Если городов нет, сразу сохраняем
      // handleSave(regionCode, null);
      setSelectedCity(selectedRegionData.cities[1].code);
    } else if (selectedRegionData) {
      setCurrentStep("city");
      // Сбрасываем город, после обновления региона. А зачем?
      // У нас всегда есть город по умолчанию, поэтому устанавливаем его
      setSelectedCity(city || null);
    }
  }, [selectedRegion, user]);

  const handleCitySelect = (cityCode) => {
    setSelectedCity(cityCode);
  };

  const handleBackToRegion = useCallback(() => {
    setCurrentStep("region");
  }, []);

  const handleSave = useCallback(() => {
    const regionData = {
      region: selectedRegion,
      city: selectedRegion === "US" ? selectedCity : "LONDON",
    };

    localStorage.setItem("userRegion", JSON.stringify(regionData));

    if (user) {
      updateCustomerInfo(regionData, user.id);
    } else {
      onSelectRegion(regionData);
    }

    clearUpcomingEventsLoaded();
    closeModal();
  }, [user, selectedRegion, selectedCity]);

  const defaultCustomStyle =
    clientWidth() < theme.responsive.breakpoints.sm
      ? {
          ...modalStyles.newMobileDefault,
          overlay: {
            ...modalStyles.newMobileDefault.overlay,
            zIndex: 1060,
          },
          content: {
            ...modalStyles.newMobileDefault.content,
            padding: "16px",
          },
        }
      : {
          ...modalStyles.default,
          overlay: {
            ...modalStyles.default.overlay,
            zIndex: 1060,
          },
          content: {
            ...modalStyles.default.content,
            width: "470px",
            padding: "16px",
          },
        };

  if (locationsLoading) {
    return null; // или спиннер
  }

  return (
    <Modal
      appElement={document.getElementById("#app")}
      isOpen={isModalOpen}
      onRequestClose={onClose}
      style={defaultCustomStyle}
      contentLabel="Choose Your Region Modal"
    >
      <div className="choose-your-region-modal tbk-rounded-lg tbk-bg-basic-white">
        {currentStep === "region" && selectedRegion === "US" && false ? (
          <button
            className="tbk-absolute tbk-left-2 tbk-text-blue-grey"
            onClick={() => setCurrentStep("city")}
          >
            <ArrowLeftIcon />
          </button>
        ) : null}
        <button
          className="tbk-absolute tbk-right-2 tbk-text-blue-grey"
          onClick={onClose}
        >
          <CloseIcon />
        </button>
        <div className="tbk-items-center tbk-justify-center tbk-p-2">
          <div
            className={`tbk-mb-4 tbk-px-4 tbk-pt-4 tbk-text-center${
              currentStep === "city" ? " tbk-hidden" : ""
            }`}
          >
            <Image className="tbk-inline-block" />
          </div>
          <div className="">
            {currentStep === "region" ? (
              <>
                <h2 className="tbk-text-title-bold md:tbk-text-h2-black tbk-mb-2 tbk-text-primary">
                  Choose Your Region
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-blue-grey">
                  We have different services in each country.
                </p>

                <div className="tbk-mb-4 tbk-space-y-0.5">
                  {locations.map((country) => (
                    <RegionItem
                      key={country.code}
                      id={`region-${country.code}`}
                      active={selectedRegion === country.code}
                      region={country.code}
                      title={country.description}
                      flagSrc={country.code === "GB" ? ukFlag : usaFlag} // или маппинг код → картинка
                      flagSrcSet={
                        country.code === "GB"
                          ? `${ukFlag} 1x, ${ukFlag2x} 2x, ${ukFlag3x} 3x`
                          : `${usaFlag} 1x, ${usaFlag2x} 2x, ${usaFlag3x} 3x`
                      }
                      onChange={handleRegionSelect}
                    />
                  ))}
                </div>

                <Button
                  variant="secondary"
                  className="tbk-w-full"
                  endIcon={<ArrowRightIcon />}
                  onClick={() => {
                    if (hasCities) {
                      setCurrentStep("city");
                    } else {
                      handleSave();
                    }
                  }}
                  disabled={!selectedRegion}
                >
                  {hasCities ? "Continue" : "Save"}
                </Button>
              </>
            ) : (
              <>
                <h2 className="tbk-text-title-bold md:tbk-text-h2-black tbk-mb-1 tbk-text-primary">
                  Your City
                </h2>
                <p className="tbk-text-main tbk-mb-2 tbk-text-blue-grey">
                  Choose your city from the list.
                </p>

                <div className="tbk-mb-4 tbk-space-y-0.5">
                  {selectedRegionData?.cities
                    ?.filter((city) => city.code !== "ALL")
                    .map((city) => (
                      <CityItem
                        key={city.code}
                        id={`city-${city.code}`}
                        active={selectedCity === city.code}
                        city={city.description}
                        onChange={() => handleCitySelect(city.code)}
                      />
                    ))}
                </div>

                <div>
                  <div className="tbk-text-small tbk-mb-1 tbk-text-primary">
                    Your Region:
                  </div>
                  <button
                    onClick={handleBackToRegion}
                    className="tbk-text-main tbk-mb-4 tbk-flex tbk-w-full tbk-items-center tbk-space-x-2 tbk-rounded-2xl tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-p-2 tbk-text-left tbk-text-blue-grey hover:tbk-bg-blue-grey-light"
                  >
                    <GlobeIcon className="tbk-text-blue-grey" />
                    <div className="">
                      <div className="tbk-text-h3-subtitle tbk-flex tbk-items-center tbk-text-primary">
                        <img
                          className="tbk-mr-1"
                          src={selectedRegion === "GB" ? ukFlag : usaFlag}
                          title={
                            selectedRegion === "US" ? "United States" : "UK"
                          }
                          srcSet={
                            selectedRegion === "GB"
                              ? `${ukFlag} 1x, ${ukFlag2x} 2x, ${ukFlag3x} 3x`
                              : `${usaFlag} 1x, ${usaFlag2x} 2x, ${usaFlag3x} 3x`
                          }
                          alt={region}
                          style={{ width: "24px", height: "24px" }}
                        />
                        {selectedRegion === "US" ? "United States" : "UK"}
                      </div>
                      <div className="tbk-flex tbk-items-center">
                        <div className="tbk-text-main-linked hover:tbk-text-blue-light">
                          Change Region
                        </div>
                        <ArrowRightIcon className="tbk-ml-0.5 tbk-text-blue-grey" />
                      </div>
                    </div>
                  </button>
                </div>

                <Button
                  variant="secondary"
                  className="tbk-w-full"
                  endIcon={<ArrowRightIcon />}
                  onClick={handleSave}
                  disabled={
                    hasCities && !selectedCity // ||
                  }
                  // disabled={!selectedCity}
                >
                  Save
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

// Функция для конвертации города в бэкенд-формат
const convertCityToBackendFormat = (city) => {
  if (!city) return null;
  return CITY_MAPPING[city] || null;
};

RegionSelectModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSelectRegion: PropTypes.func,
  region: PropTypes.string,
};

export default RegionSelectModal;
